import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';

const About: React.FC = () => {
  const [showEarlierWork, setShowEarlierWork] = useState(false);

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const copyEmailToClipboard = () => {
    const email = 'lee.sanggean@gmail.com';
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(email)
        .then(() => {
          toast.success('Email copied to clipboard!');
        })
        .catch((err) => {
          console.error('Copy failed:', err);
          toast.error('Failed to copy email.');
        });
    } else {
      const input = document.createElement('input');
      input.value = email;
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      try {
        const result = document.execCommand('copy');
        if (result) {
          toast.success('Email copied to clipboard!');
        } else {
          throw new Error('execCommand failed');
        }
      } catch (err) {
        console.error('Fallback copy failed:', err);
        toast.error('Clipboard not supported in this browser.');
      } finally {
        document.body.removeChild(input);
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      copyEmailToClipboard();
    }
  };

  return (
    <>
      <Helmet>
        <title>Sangjin Lee | About</title>
        <meta
          name="description"
          content="Full-stack developer and product builder. From architecture to AI-powered platforms."
        />
        <meta property="og:title" content="Sangjin Lee | About" />
        <meta
          property="og:description"
          content="I've spent 15 years learning how people move through spaces — physical, commercial, and digital. Now I build the digital ones."
        />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://sjlee.co.kr/images/og/main-og-image.png" />
        <meta property="og:url" content="https://sjlee.co.kr/about" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <div className="pt-24 px-4 sm:px-6 lg:px-8 min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="text-center pt-20 pb-20"
          >
            <h1 className="text-3xl sm:text-4xl font-light mb-6 tracking-tight text-gray-800 dark:text-gray-200">
              Sangjin Lee
            </h1>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-200 font-light leading-relaxed max-w-3xl mx-auto mb-6">
              I've always been more curious about what's next<br />
              than comfortable with what's now.
            </p>
            <p className="text-base sm:text-lg text-gray-800 dark:text-gray-200 font-medium mb-8">
              Architecture. Strategy. Code.<br />
              That curiosity built all three.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/portfolio"
                className="px-4 py-2 bg-gray-800 dark:bg-gray-700 text-white rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors duration-200 text-center text-sm"
              >
                View My Work
              </Link>
            </div>
          </motion.section>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerChildren}
            className="space-y-20"
          >
            {/* Story Section */}
            <motion.section variants={fadeInUp} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 sm:p-12">
              <h2 className="text-2xl sm:text-3xl font-light text-gray-800 dark:text-gray-200 mb-6">
                Not all paths are straight. Mine went through three lives.
              </h2>
              <p className="text-base text-gray-700 dark:text-gray-200 mb-4">
                Not because I couldn't commit —<br />
                but because I've always been more afraid of standing still<br />
                than of starting over.
              </p>
              <div className="border-l-4 border-gray-300 dark:border-gray-600 pl-6 mb-12">
                <p className="text-base text-gray-700 dark:text-gray-200 leading-relaxed">
                  Each turn was a question I needed to answer.<br />
                  Turns out, they were all asking the same thing:<br />
                  how do you design something that truly works for people?
                </p>
              </div>

              <div>
                {/* Chapter 01 */}
                <div className="mb-10">
                  <h3 className="text-sm font-bold uppercase tracking-widest text-gray-500 dark:text-gray-200 mb-4">
                    Chapter 01 — I designed spaces
                  </h3>
                  <p className="text-gray-700 dark:text-gray-200 text-base leading-relaxed">
                    Architecture taught me one thing above all:<br />
                    a beautiful design that ignores human behavior is just art.
                  </p>
                  <p className="text-gray-700 dark:text-gray-200 text-base leading-relaxed mt-4">
                    I spent years learning to design around how people<br />
                    actually move, decide, and feel.<br />
                    That instinct never left me.
                  </p>
                </div>

                {/* Divider */}
                <div className="h-px bg-gray-200 dark:bg-gray-800 my-10"></div>

                {/* Chapter 02 */}
                <div className="mb-10">
                  <h3 className="text-sm font-bold uppercase tracking-widest text-gray-500 dark:text-gray-200 mb-4">
                    Chapter 02 — I scaled brands
                  </h3>
                  <p className="text-gray-700 dark:text-gray-200 text-base leading-relaxed">
                    Nearly a decade running retail strategy for Samsung and LG.<br />
                    13 countries. 350+ stores. ~$1M budgets.
                  </p>
                  <p className="text-gray-700 dark:text-gray-200 text-base leading-relaxed mt-4">
                    The job wasn't design — it was alignment.<br />
                    Getting clients, vendors, and local teams<br />
                    across Dubai, Lagos, and Johannesburg<br />
                    to agree on what "done" looked like.
                  </p>
                  <p className="text-gray-700 dark:text-gray-200 text-base leading-relaxed mt-4">
                    One data-driven pilot in Dubai: sales up 38%.
                  </p>
                </div>

                {/* Divider */}
                <div className="h-px bg-gray-200 dark:bg-gray-800 my-10"></div>

                {/* Chapter 03 */}
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-widest text-gray-500 dark:text-gray-200 mb-4">
                    Chapter 03 — I build products
                  </h3>
                  <p className="text-gray-700 dark:text-gray-200 text-base leading-relaxed">
                    Now I build digital products.
                  </p>
                  <p className="text-gray-700 dark:text-gray-200 text-base leading-relaxed mt-4">
                    In 2025, I launched <a href="https://dwriting.ai" target="_blank" rel="noopener noreferrer" className="text-gray-800 dark:text-gray-200 font-semibold hover:text-gray-600 dark:hover:text-gray-400 underline transition-colors">dwriting.ai</a> — an AI-powered platform<br />
                    I architected and built solo, leveraging AI as my<br />
                    ultimate co-developer.
                  </p>
                  <p className="text-gray-700 dark:text-gray-200 text-base leading-relaxed mt-4">
                    From system design to deployment.<br />
                    Real users. Real data. Still growing.
                  </p>
                  <p className="text-gray-800 dark:text-gray-200 text-lg font-medium leading-relaxed mt-8">
                    The medium changed. The instinct didn't.
                  </p>
                </div>
              </div>
            </motion.section>

            {/* What I Bring Section */}
            <motion.section variants={fadeInUp}>
              <h2 className="text-2xl sm:text-3xl font-light text-gray-800 dark:text-gray-200 mb-4">
                What makes me different isn't just the tech stack.
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-200 mb-12">
                It's what I bring to how I use it.
              </p>

              <div className="space-y-8">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                  <div className="flex items-start gap-4">
                    <span className="text-2xl">🏗</span>
                    <div>
                      <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-2">
                        Architectural thinking
                      </h3>
                      <p className="text-gray-600 dark:text-gray-200 text-sm leading-relaxed">
                        I design systems the way I used to design buildings —<br />
                        structure first, details second.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                  <div className="flex items-start gap-4">
                    <span className="text-2xl">🌍</span>
                    <div>
                      <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-2">
                        Global perspective
                      </h3>
                      <p className="text-gray-600 dark:text-gray-200 text-sm leading-relaxed">
                        I've lived and worked across South Korea, India, UAE, South Africa, and the UK.<br />
                        I don't assume. I observe.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                  <div className="flex items-start gap-4">
                    <span className="text-2xl">⚡</span>
                    <div>
                      <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-2">
                        Builder's bias
                      </h3>
                      <p className="text-gray-600 dark:text-gray-200 text-sm leading-relaxed">
                        I don't just plan. I ship.<br />
                        <a href="https://dwriting.ai" target="_blank" rel="noopener noreferrer" className="text-gray-600 dark:text-gray-200 hover:text-gray-800 dark:hover:text-gray-200 underline transition-colors">dWriting.ai</a> went from idea to production with real users in under a year.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.section>

            {/* Skills Section */}
            <motion.section variants={fadeInUp}>
              <h2 className="text-2xl sm:text-3xl font-light text-gray-800 dark:text-gray-200 mb-12">
                What I work with
              </h2>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 space-y-8">
                <div>
                  <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">AI/ML Integration</h3>
                  <p className="text-gray-600 dark:text-gray-200 text-sm">
                    Google Gemini API · Prompt Engineering · Statistical Analysis · Outlier Detection
                  </p>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">Development</h3>
                  <p className="text-gray-600 dark:text-gray-200 text-sm">
                    React 18 · TypeScript · Node.js · Express · MongoDB · Python · REST APIs
                  </p>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">Infrastructure</h3>
                  <p className="text-gray-600 dark:text-gray-200 text-sm">
                    Docker · Railway · MongoDB Atlas · Firebase · AWS
                  </p>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">Analytics</h3>
                  <p className="text-gray-600 dark:text-gray-200 text-sm">
                    Google Analytics · Pandas · Numpy · Matplotlib · Seaborn
                  </p>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">Design</h3>
                  <p className="text-gray-600 dark:text-gray-200 text-sm">
                    SketchUp · AutoCAD · Photoshop · Illustrator · Final Cut Pro
                  </p>
                </div>
              </div>
            </motion.section>

            {/* Experience Section */}
            <motion.section variants={fadeInUp}>
              <h2 className="text-2xl sm:text-3xl font-light text-gray-800 dark:text-gray-200 mb-12">
                Experience
              </h2>

              {/* Builder Chapter */}
              <div className="mb-16">
                <div className="flex items-center gap-4 mb-8">
                  <div className="h-px bg-gray-300 dark:bg-gray-800 flex-1"></div>
                  <h3 className="text-sm font-bold uppercase tracking-widest text-gray-500 dark:text-gray-200">
                    Builder
                  </h3>
                  <div className="h-px bg-gray-300 dark:bg-gray-800 flex-1"></div>
                </div>

                <div className="space-y-8">
                  {/* dWriting.ai */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                    <div className="flex flex-col md:flex-row justify-between gap-4 mb-4">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-1">
                          <a href="https://dwriting.ai" target="_blank" rel="noopener noreferrer" className="text-gray-800 dark:text-gray-200 hover:text-gray-600 dark:hover:text-gray-400 transition-colors">
                            dWriting.ai
                          </a>
                        </h4>
                        <p className="text-gray-600 dark:text-gray-200 text-sm">Full-Stack Developer & Product Lead</p>
                      </div>
                      <div className="text-right text-gray-400 dark:text-gray-200 italic text-sm">
                        2024 – Present<br />
                        Woking, UK
                      </div>
                    </div>
                    <p className="text-gray-700 dark:text-gray-200 mb-4 leading-relaxed">
                      AI-powered writing practice platform. Built solo from architecture to deployment.
                    </p>
                    <ul className="space-y-2 text-gray-600 dark:text-gray-200 text-sm mb-4">
                      <li>→ Reduced AI score variance by 40% via statistical outlier detection</li>
                      <li>→ Improved email delivery rate from 60% to 98%</li>
                      <li>→ 15,000+ lines of code, 70+ React components, 30+ API endpoints</li>
                      <li>→ 99.9% uptime, 200+ registered users</li>
                    </ul>
                    <p className="text-sm text-gray-500 dark:text-gray-200">
                      Stack: React 18, TypeScript, Node.js, Express, MongoDB, Google Gemini API, Docker, Railway
                    </p>
                  </div>

                  {/* Digiocean */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                    <div className="flex flex-col md:flex-row justify-between gap-4">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-1">
                          Digiocean
                        </h4>
                        <p className="text-gray-600 dark:text-gray-200 text-sm">Co-Founder & Chief Operator</p>
                        <p className="text-gray-700 dark:text-gray-200 text-sm mt-2 leading-relaxed">
                          Educational content brand and platform.<br />
                          Strategy, development, and operations.
                        </p>
                      </div>
                      <div className="text-right text-gray-400 dark:text-gray-200 italic text-sm">
                        2023 – Present<br />
                        London, UK
                      </div>
                    </div>
                  </div>

                  {/* Eduocean */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                    <div className="flex flex-col md:flex-row justify-between gap-4">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-1">
                          Eduocean
                        </h4>
                        <p className="text-gray-600 dark:text-gray-200 text-sm">Co-Founder & Developer</p>
                        <p className="text-gray-700 dark:text-gray-200 text-sm mt-2 leading-relaxed">
                          E-learning platform built with Django, Bootstrap, AWS.<br />
                          My first step from designer to builder.
                        </p>
                      </div>
                      <div className="text-right text-gray-400 dark:text-gray-200 italic text-sm">
                        2020 – 2021<br />
                        London, UK
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Strategist Chapter */}
              <div className="mb-16">
                <div className="flex items-center gap-4 mb-8">
                  <div className="h-px bg-gray-300 dark:bg-gray-800 flex-1"></div>
                  <h3 className="text-sm font-bold uppercase tracking-widest text-gray-500 dark:text-gray-200">
                    Strategist
                  </h3>
                  <div className="h-px bg-gray-300 dark:bg-gray-800 flex-1"></div>
                </div>

                <div className="space-y-8">
                  {/* Cheil Europe */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                    <div className="flex flex-col md:flex-row justify-between gap-4">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-1">
                          Cheil Europe (Samsung)
                        </h4>
                        <p className="text-gray-600 dark:text-gray-200 text-sm">Head of Retail</p>
                        <p className="text-gray-700 dark:text-gray-200 text-sm mt-2 leading-relaxed">
                          Led Samsung UK retail marketing. Built new business proposals,<br />
                          managed retail operations end-to-end.
                        </p>
                      </div>
                      <div className="text-right text-gray-400 dark:text-gray-200 italic text-sm">
                        2022 – 2023<br />
                        London, UK
                      </div>
                    </div>
                  </div>

                  {/* HS Ad */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                    <div className="flex flex-col md:flex-row justify-between gap-4">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-1">
                          HS Ad, an LG Company
                        </h4>
                        <p className="text-gray-600 dark:text-gray-200 text-sm">Senior Retail Manager</p>
                        <p className="text-gray-700 dark:text-gray-200 text-sm mt-2 leading-relaxed">
                          Omnichannel retail strategy across 13 countries, 313 stores.<br />
                          Store design, revenue analysis, brand renovations.
                        </p>
                      </div>
                      <div className="text-right text-gray-400 dark:text-gray-200 italic text-sm">
                        2021 – 2022 · Johannesburg, SA<br />
                        2015 – 2019 · Dubai, UAE
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Designer Chapter */}
              <div>
                <div className="flex items-center gap-4 mb-8">
                  <div className="h-px bg-gray-300 dark:bg-gray-800 flex-1"></div>
                  <h3 className="text-sm font-bold uppercase tracking-widest text-gray-500 dark:text-gray-200">
                    Designer
                  </h3>
                  <div className="h-px bg-gray-300 dark:bg-gray-800 flex-1"></div>
                </div>

                <div className="space-y-8">
                  {/* One Agency */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                    <div className="flex flex-col md:flex-row justify-between gap-4">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-1">
                          One Agency (Cheil Worldwide)
                        </h4>
                        <p className="text-gray-600 dark:text-gray-200 text-sm">Space Designer</p>
                        <p className="text-gray-700 dark:text-gray-200 text-sm mt-2 leading-relaxed">
                          Samsung retail environments and launch events.<br />
                          Where architecture met brand experience.
                        </p>
                      </div>
                      <div className="text-right text-gray-400 dark:text-gray-200 italic text-sm">
                        2013 – 2015<br />
                        Dubai, UAE
                      </div>
                    </div>
                  </div>

                  {/* Earlier Work */}
                  {showEarlierWork ? (
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 space-y-6">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-1">
                          Jungdo Civil & Architecture Engineering
                        </h4>
                        <p className="text-gray-600 dark:text-gray-200 text-sm">Civil Engineer</p>
                        <p className="text-gray-700 dark:text-gray-200 text-sm mt-2 leading-relaxed">
                          Managed construction and inspection.<br />
                          Major Project: Samsung sports park renovation.
                        </p>
                        <p className="text-gray-400 dark:text-gray-200 italic text-sm mt-2">
                          July 2013 – Sept. 2013 · Suwon, South Korea
                        </p>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-1">
                          MAP Group
                        </h4>
                        <p className="text-gray-600 dark:text-gray-200 text-sm">Architectural Engineer</p>
                        <p className="text-gray-700 dark:text-gray-200 text-sm mt-2 leading-relaxed">
                          Managed construction and inspection.<br />
                          Major Projects: Construction of Valeo automotive plant, basic design of DAS new plant building.
                        </p>
                        <p className="text-gray-400 dark:text-gray-200 italic text-sm mt-2">
                          Nov. 2012 – Feb. 2013 · Chennai, India
                        </p>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-1">
                          D·Lim Architecture
                        </h4>
                        <p className="text-gray-600 dark:text-gray-200 text-sm">Designer</p>
                        <p className="text-gray-700 dark:text-gray-200 text-sm mt-2 leading-relaxed">
                          Focused on design, analysis, case studies, design competitions, and modeling.<br />
                          Major Projects: Ahn Jung-Geun Memorial Hall, Gang-nam residential urban development.
                        </p>
                        <p className="text-gray-400 dark:text-gray-200 italic text-sm mt-2">
                          Mar. 2009 – Dec. 2010 · Seoul, South Korea
                        </p>
                      </div>
                      <button
                        onClick={() => setShowEarlierWork(false)}
                        className="text-gray-500 dark:text-gray-200 hover:text-gray-700 dark:hover:text-gray-300 text-sm"
                      >
                        [ - show less ]
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowEarlierWork(true)}
                      className="text-gray-500 dark:text-gray-200 hover:text-gray-700 dark:hover:text-gray-300 text-sm"
                    >
                      [ + show more ]
                    </button>
                  )}
                </div>
              </div>
            </motion.section>

            {/* Education Section */}
            <motion.section variants={fadeInUp}>
              <h2 className="text-2xl sm:text-3xl font-light text-gray-800 dark:text-gray-200 mb-12">
                Education
              </h2>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 space-y-6">
                <div>
                  <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-1">
                    MSc Computing & Information Systems · Merit
                  </h3>
                  <p className="text-gray-600 dark:text-gray-200 text-sm mb-2">Queen Mary University of London · 2019–2020</p>
                  <p className="text-gray-700 dark:text-gray-200 text-sm">
                    Thesis: Evaluating the Effectiveness of a Customized E-Learning Platform
                  </p>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-1">
                    Master of Architecture
                  </h3>
                  <p className="text-gray-600 dark:text-gray-200 text-sm">Hongik University, Seoul · 2009–2011</p>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-1">
                    Bachelor of Engineering, Architecture
                  </h3>
                  <p className="text-gray-600 dark:text-gray-200 text-sm">Namseoul University · 2003–2009</p>
                </div>
              </div>
            </motion.section>

            {/* Closing / CTA Section */}
            <motion.section variants={fadeInUp} className="text-center pb-20">
              <p className="text-base text-gray-700 dark:text-gray-200 mb-4">
                I'm currently based in Woking, UK.
              </p>
              <p className="text-base text-gray-700 dark:text-gray-200 mb-4">
                Building products. Open to what's next.
              </p>
              <p className="text-xl font-light text-gray-800 dark:text-gray-200">
                Let's talk.
              </p>
            </motion.section>
          </motion.div>

          {/* Scroll to Top Button */}
          <motion.button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-6 right-6 w-12 h-12 flex items-center justify-center bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all z-50"
            aria-label="Back to top"
            variants={fadeInUp}
          >
            <svg
              className="w-5 h-5 text-gray-700 dark:text-gray-200"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 10l7-7m0 0l7 7m-7-7v18"
              />
            </svg>
          </motion.button>
        </div>
      </div>
    </>
  );
};

export default About;
